import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import PageHeader from '../components/PageHeader';
import { loadProjects } from '../utils/contentLoader';

const CATEGORY_META = {
  university: { label: 'University', color: '#8a9fd4' },
  work:       { label: 'Work',       color: '#7cb8a0' },
  personal:   { label: 'Personal',   color: '#c4a24a' },
};

const FILTERS = ['all', 'university', 'work', 'personal'];

// Persist filter within the same browser session; resets on page reload.
function readSession(key, fallback) {
  try { const v = sessionStorage.getItem(key); return v !== null ? JSON.parse(v) : fallback; } catch { return fallback; }
}
function writeSession(key, value) {
  try { sessionStorage.setItem(key, JSON.stringify(value)); } catch {}
}

export default function Projects() {
  const [activeFilter, setActiveFilter] = useState(() => readSession('projects_filter', 'all'));
  const [showFeaturedOnly, setShowFeaturedOnly] = useState(() => readSession('projects_featured', false));

  function handleFilter(f) { setActiveFilter(f); writeSession('projects_filter', f); }
  function handleFeatured() {
    setShowFeaturedOnly((v) => { const next = !v; writeSession('projects_featured', next); return next; });
  }

  const allProjects = useMemo(() => loadProjects(), []);

  const filtered = useMemo(() => {
    return allProjects.filter((p) => {
      if (showFeaturedOnly && !p.featured) return false;
      if (activeFilter !== 'all' && p.category !== activeFilter) return false;
      return true;
    });
  }, [allProjects, activeFilter, showFeaturedOnly]);

  return (
    <div style={{ padding: '48px' }}>
      <PageHeader
        eyebrow="03 — Projects"
        title="Projects"
        subtitle="Things I built, broke, fixed, and learned from."
      />

      {/* Filter bar */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        flexWrap: 'wrap',
        marginBottom: '36px',
      }}>
        {FILTERS.map((f) => (
          <button
            key={f}
            onClick={() => handleFilter(f)}
            style={{
              fontFamily: '"JetBrains Mono", monospace',
              fontSize: '0.65rem',
              letterSpacing: '0.06em',
              padding: '5px 12px',
              borderRadius: '4px',
              border: '1px solid',
              cursor: 'pointer',
              background: activeFilter === f ? 'var(--gold)' : 'transparent',
              borderColor: activeFilter === f ? 'var(--gold)' : 'var(--border)',
              color: activeFilter === f ? '#0d0c10' : 'var(--text-3)',
              transition: 'all 0.15s ease',
              textTransform: 'uppercase',
            }}
          >
            {f}
          </button>
        ))}

        {/* Separator */}
        <div style={{ width: '1px', height: '20px', background: 'var(--border)', margin: '0 4px' }} />

        {/* Featured toggle */}
        <button
          onClick={handleFeatured}
          style={{
            fontFamily: '"JetBrains Mono", monospace',
            fontSize: '0.65rem',
            letterSpacing: '0.06em',
            padding: '5px 12px',
            borderRadius: '4px',
            border: '1px solid',
            cursor: 'pointer',
            background: showFeaturedOnly ? 'var(--gold-dim)' : 'transparent',
            borderColor: showFeaturedOnly ? 'var(--gold)' : 'var(--border)',
            color: showFeaturedOnly ? 'var(--gold)' : 'var(--text-3)',
            transition: 'all 0.15s ease',
            textTransform: 'uppercase',
          }}
        >
          ★ Featured
        </button>

        <span style={{
          fontFamily: '"JetBrains Mono", monospace',
          fontSize: '0.6rem',
          color: 'var(--text-3)',
          marginLeft: 'auto',
        }}>
          {filtered.length} project{filtered.length !== 1 ? 's' : ''}
        </span>
      </div>

      {/* Projects grid */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
        gap: '12px',
      }}>
        {filtered.map((project) => {
          const meta = CATEGORY_META[project.category] || CATEGORY_META.personal;
          return (
            <Link
              key={project.slug}
              to={`/projects/${project.slug}`}
              style={{ textDecoration: 'none' }}
            >
              <div
                className="card-hover"
                style={{
                  background: 'var(--surface)',
                  border: '1px solid var(--border)',
                  borderRadius: '8px',
                  overflow: 'hidden',
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  borderTop: `2px solid ${meta.color}`,
                }}
              >
                {/* Thumbnail */}
                {project.thumbnail && (
                  <div style={{ height: '140px', overflow: 'hidden', background: 'var(--surface-2)' }}>
                    <img
                      src={project.thumbnail}
                      alt={project.title}
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                  </div>
                )}

                <div style={{ padding: '16px', flex: 1, display: 'flex', flexDirection: 'column' }}>
                  {/* Category + year row */}
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
                    <span style={{
                      fontFamily: '"JetBrains Mono", monospace',
                      fontSize: '0.6rem',
                      letterSpacing: '0.06em',
                      color: meta.color,
                      textTransform: 'uppercase',
                    }}>
                      {meta.label}
                    </span>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      {project.featured && (
                        <span style={{ color: 'var(--gold)', fontSize: '0.7rem' }}>★</span>
                      )}
                      <span style={{
                        fontFamily: '"JetBrains Mono", monospace',
                        fontSize: '0.6rem',
                        color: 'var(--text-3)',
                      }}>
                        {project.year}
                      </span>
                    </div>
                  </div>

                  {/* Title */}
                  <h2 style={{
                    fontFamily: '"Cormorant Garamond", Georgia, serif',
                    fontSize: '1.25rem',
                    fontWeight: 600,
                    color: 'var(--text)',
                    margin: '0 0 8px',
                    letterSpacing: '-0.01em',
                    lineHeight: 1.2,
                  }}>
                    {project.title}
                  </h2>

                  {/* Description */}
                  {project.description && (
                    <p style={{
                      fontFamily: '"Plus Jakarta Sans", system-ui, sans-serif',
                      fontSize: '0.75rem',
                      color: 'var(--text-3)',
                      margin: '0 0 12px',
                      lineHeight: 1.5,
                      flex: 1,
                    }}>
                      {project.description}
                    </p>
                  )}

                  {/* Tags */}
                  {project.tags && project.tags.length > 0 && (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', marginTop: 'auto' }}>
                      {project.tags.slice(0, 4).map((tag) => (
                        <span key={tag} className="tag">{tag}</span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </Link>
          );
        })}
      </div>

      {filtered.length === 0 && (
        <div style={{
          textAlign: 'center',
          padding: '60px',
          color: 'var(--text-3)',
          fontFamily: '"Cormorant Garamond", Georgia, serif',
          fontSize: '1.2rem',
          fontStyle: 'italic',
        }}>
          No projects match the current filter.
        </div>
      )}
    </div>
  );
}
